import configFlags from './configFlags'

type InferType<T> = {
  [K in keyof T]: T[K] extends object ? InferType<T[K]> : boolean
}

export enum FlagType {
  Feature = 'feature',
  Automation = 'automation',
  Untracked = 'untracked',
  Club = 'club',
}

export enum Feature {
  Fullstory = 'fullstory',
  MI = 'mi',
  HideClientList = 'hideClientList',
  NewProtectionSourcing = 'new_protection_sourcing',
  AddIntroducer = 'add_introducer',
  InviteToScheduleAppointment = 'inviteToScheduleAppointment',
  Conveyancing = 'conveyancing',
  KbaFlow = 'kbaFlow',
  Network = 'network',
  Lender = 'lender',
  BulkIntroducers = 'bulk_introducers',
  Surveying = 'surveying',
  // SurveyingCp's actual value in CP FE is calculated based on both Surveying and SurveyingCp's value
  // look at calculateSurveyingCpFlag in common/utils/src/flags/flags.helpers.ts
  SurveyingCp = 'surveyingCp',
  Integrations = 'integrations',
  ReferWills = 'refer_wills',
  ReferWillsCpOnly = 'refer_wills_cp_only',
  PreviewMode = 'preview_mode',
  NeedAPro = 'need_pro',
  MovingCompany = 'moving_company_referral',
  ConveyancingClientPortal = 'conveyancing_client_portal',
  CreditReportCp = 'credit_report_cp',
  TurnOffReportProblem = 'turn_off_report_problem',
  SmartlookCP = 'smartlook_cp',
  HomebuyersChecklist = 'homebuyers_checklist',
  AccountingSearchMode = 'accounting_search_mode',
  AdditionalCaseFilters = 'additional_case_filters',
  ReportCommissions = 'report_commissions',
  Dashboards = 'dashboards',
  HidePredictedRevenue = 'hide_predicted_revenue',
  Payments = 'payments',
  HideLandRegistryPurchase = 'hide_land_registry_purchase',
  CaseSidebarDrawer = 'case_sidebar_drawer',
  AddingManualGI = 'adding_manual_gi',
  StandaloneGI = 'standalone_gi',
  EnhancedAutoRefer = 'enhanced_autorefer',
  HideOutOfPanelProcs = 'hide_out_of_panel_procs',
  HomeInOne = 'homeInOne',
  CommissionOptions = 'commission_options',
  MultiOrgReport = 'multi_org_report',
  NewPanelManagementAccordion = 'new_panel_management_accordion',
  ConveyancingScotland = 'conveyancing_scotland',
  AdvancedPermissions = 'advanced_permissions',
  XlsReport = 'xls_report',
  DataSecurityReport = 'data_security_report',
  AllowARToggleIncludeDirect = 'allow_ar_toggle_include_direct',
  DocumentRequirements = 'document_requirements',
  CreditRefresh = 'credit_refresh',
  RiskReport = 'risk_report',
  CaseClientSurvey = 'case_client_survey',
  PmiOnly = 'pmi_only',
  MMDConveyancing = 'MMD_conveyancing',
  HealthFactFindCp = 'health_fact_find_cp',
  SbgRmar = 'sbg_rmar',
  ClientUpdateBanner = 'client_update_banner',
  ReferralPartnerships = 'in_network_referrals',
  ReferralPartnershipsSettings = 'in_network_referrals_settings',
  NewHealthAndProtectionPage = 'new_health_and_protection_page',
  EidvDonors = 'eidv_donors',
  LV = 'lv',
  NewProductPage = 'new_product_page',
  MockModeEidv = 'mock_mode_eidv',
  ProtectionNeedsSidebar = 'protection_needs_sidebar',
  ProtectionV2 = 'protection_v2',
  ProtectionIPipeline = 'protection_ipipeline',
  MistralDocumentUpload = 'mistral_document_upload',
  ProtectionV2UsePanel = 'protection_v2_use_panel',
  AcreReferralClub = 'acre_referral_club',
  LBGVerifications = 'lbg_verifications',
  ProtectionAdviceDisclaimer = 'protection_advice_disclaimer',
  ExtMarketingReports = 'ext_marketing_reports',
  ClientPortalChangesModal = 'client_portal_changes_modal',
  MeetingAI = 'meeting_ai',
  ProtectionAddendum = 'protection_addendum',
}

export enum Automation {
  InviteClient = 'invite_client',
}

export enum Untracked {
  ARIsTradingStyle = 'ar_is_trading_style',
  ARIsSubdivision = 'ar_is_subdivision',
}

export enum LenderFlag {
  Santander = 'santander',
  NatWest = 'natwest',
  Nationwide = 'nationwide',
  NationwideFMA = 'nationwideFMA',
  Lloyds = 'lloyds',
}

export enum Club {
  PMS13 = 'pms13',
  PMSIPFA = 'pmsipfa',
  PMSDIGITAL = 'pmsdigital',
  PMSEXT = 'pmsext',
  FPG = 'fpg',
  RightMortgage = 'rightmortgage',
  MMD = 'mmd',
  CONNELLS = 'connells',
  Sesame = 'sesame',
}

// export type Flags = {
//   [FlagType.Feature]: {[P in keyof Feature]: Feature[P]}
//   [FlagType.Automation]: {[P in keyof Automation]: Automation[P]}
//   [FlagType.Untracked]: {[P in keyof Untracked]: Untracked[P]}
//   [FlagType.Club]: {[P in keyof Club]: Club[P]}

// }

export const featureFlagDescriptions: { [key in Feature | Automation | Club | Untracked]: string } = {
  [Feature.Fullstory]: 'Enable/Disable Fullstory analytics',
  [Feature.MI]: 'Enable/Disable viewing the reporting tabs. Please note additional permissions are required.',
  [Feature.HideClientList]: 'Enable/Disable hiding the client list. Please note additional permissions are required.',
  [Feature.NewProtectionSourcing]:
    'Enable/Disable New Protection Sourcing: Old sourcing is deprecated. No longer supported/maintained.', // Depreciated
  [Feature.AddIntroducer]: 'Enable/Disable the ability to create and modify introducer organisations.',
  [Feature.InviteToScheduleAppointment]:
    'Enable/Disable the ability invite clients to book time directly with customers.',
  [Feature.Conveyancing]: 'Enable/Disable the ability to refer clients to a conveyancer',
  [Feature.Network]: 'Enable/disable access to network mgmt functionality for DA Organisations.',
  [Feature.KbaFlow]:
    'Enable/Disable the KBA flow feature in the credit section of the fact find (Subject to Compliance Requirements)',
  [Feature.Lender]: 'Enable access to specific lender for DIP/FMA purposes',
  [Feature.BulkIntroducers]: 'Enable access to the bulk upload form for introducers',
  [Automation.InviteClient]: 'Auto-invite client to CP on case creation.',
  [Feature.Surveying]:
    'Enable/Disable referral for surveys in both crm and client portal (can be overriden by SurveyingCp in client portal)',
  [Feature.SurveyingCp]: 'Enable/Disable referral for surveys in client portal (will override Surveying)',
  [Feature.Integrations]: 'Enable access to create and manage oauth users and apps (Zapier etc)',
  [Feature.ReferWills]: 'Enable/Disable referral for wills in both crm & client portal',
  [Feature.ReferWillsCpOnly]: 'Enable/Disable referral for wills in the client portal only',
  [Feature.PreviewMode]: 'Enable/Disable preview mode for the Broker CRM',
  [Feature.NeedAPro]: 'Enable/Disable referrals to professional tradesman',
  [Feature.MovingCompany]: 'Enable/Disable referrals to moving companies',
  [Feature.ConveyancingClientPortal]: 'Enable/Disable the ability to refer clients to a conveyancer',
  [Feature.CreditReportCp]: 'Enable/Disable credit report summary in the client portal', // Depreciated
  [Feature.TurnOffReportProblem]: 'Turn On/Off problem reporting to Zandesk feature',
  [Feature.SmartlookCP]: 'Turn On/Off Smartlook feature for CP',
  [Feature.HomebuyersChecklist]: 'Enable/Disable homebuyers checklist',
  [Feature.AccountingSearchMode]: 'Enable/Disable preview mode for new accounting search',
  [Feature.AdditionalCaseFilters]: 'Enable/Disable additional case board/list filters',
  [Feature.ReportCommissions]: 'Enable/Disable commissions section on reports tab',
  [Feature.Dashboards]: 'Enable/Disable access to the new search-powered dashboards',
  [Feature.HidePredictedRevenue]: 'Display/Hide the predicted revenue field on case items on the case board',
  [Feature.HideLandRegistryPurchase]: 'Display/hide the buy button for land registry property data',
  [Feature.AddingManualGI]: 'Enable/Disable adding manual GI for mortgage cases (CTO Approval Required)',
  [Untracked.ARIsTradingStyle]:
    'Enable/Disable AR is Trading Style, which in turn affects the wording on certain templates.',
  [Untracked.ARIsSubdivision]:
    'Enable/Disable AR is Subdivision, which in turn affects the wording on certain templates.',
  [Club.FPG]: 'Enable/Disable FPG club',
  [Club.PMS13]: 'Enable/Disable PMS 13 club',
  [Club.PMSIPFA]: 'Enable/Disable PMS IPFA club',
  [Club.PMSEXT]: 'Enable/Disable PMS EXT club',
  [Club.PMSDIGITAL]: 'Enable/Disable PMS Digital club',
  [Club.RightMortgage]: 'Enable/Disable Right Mortgage club',
  [Club.MMD]: 'Enable/Disable MMD club',
  [Club.CONNELLS]: 'Enable/Disable Connells club',
  [Club.Sesame]: 'Enable/Disable Sesame panel',
  [Feature.Payments]: 'Enable/Disable payments through GoCardless',
  [Feature.CaseSidebarDrawer]: 'Enable/Disable case sidebar drawer',
  [Feature.StandaloneGI]: 'Enable/Disable the creation of standalone GI case',
  [Feature.EnhancedAutoRefer]: 'Enable/Disable access to auto-refer for Conveyancing and Wills',
  [Feature.HideOutOfPanelProcs]: 'Enable/Disable the hiding of out of panel proc fees in sourcing',
  [Feature.HomeInOne]: 'Enable/Disable GI sourcing through Home in One',
  [Feature.CommissionOptions]: 'Enable/Disable commission options',
  [Feature.MultiOrgReport]: 'Enable/Disable other reports new endpoint',
  [Feature.NewPanelManagementAccordion]: 'Enable/Disable new panel management accordion',
  [Feature.ConveyancingScotland]:
    'Enable/Disable access to Smoove in Scotland. Also requires conveyancing to be enabled.',
  [Feature.AdvancedPermissions]: 'Enable/Disable advanced permissions',
  [Feature.XlsReport]: 'Enable/Disable XLS report',
  [Feature.DataSecurityReport]: 'Enable/Disable data security report',
  [Feature.AllowARToggleIncludeDirect]: 'Enable/Disable Allow ARs to toggle Include Direct when sourcing',
  [Feature.DocumentRequirements]: 'Enable/Disable Allow DA to review document requirements in company settings',
  [Feature.CreditRefresh]: 'Enable/Disable Allow credit refresh to be turned on',
  [Feature.RiskReport]: 'Enable/Disable Risk Report',
  [Feature.CaseClientSurvey]: 'Enable/Disable Case Client Survey',
  [Feature.PmiOnly]: 'Enable/Disable Required Task for agreed monthly protection budget for PMI cases',
  [Feature.MMDConveyancing]:
    'Enable/Disable MMD specific conveyancing referrals (only enable PPL and Smoove for remo or BTL remo case). Also requires the corresponding conveyancing flag to be enabled.',
  [Feature.HealthFactFindCp]: 'Enable/Disable Health Fact Find section in the client portal',
  [Feature.SbgRmar]: 'Enable/Disable rendering SBG only fields in RMAR reports',
  [Feature.ClientUpdateBanner]: 'Show a banner on case dashboard / case page when client has recently updated',
  [Feature.ReferralPartnerships]: 'Enable/Disable referral partnerships manager in company settings',
  [Feature.ReferralPartnershipsSettings]:
    'Enable/Disable referral partnerships settings visibility in company settings at DA level without enabling the feature',
  [Feature.NewHealthAndProtectionPage]: 'Toggles old/new Health & Protection page in the fact find.',
  [Feature.EidvDonors]: 'Enable/Disable running EIDV for donors',
  [Feature.LV]: 'Allow creds for LV to be set',
  [Feature.NewProductPage]: 'Toggles old/new Protection Products page',
  [Feature.MockModeEidv]: 'Toggles mock mode for EIDV',
  [Feature.ProtectionNeedsSidebar]: 'Toggles protection needs section in the case sidebar drawer',
  [Feature.ProtectionV2]: 'Enable protection V2 sourcing',
  [Feature.ProtectionIPipeline]: 'Use iPipeline as protection sourcing integrator',
  [Feature.MistralDocumentUpload]: 'Enable/Disable Mistral document upload',
  [Feature.ProtectionV2UsePanel]: 'Toggles protectionv2 use panel',
  [Feature.AcreReferralClub]: 'Enable/Disable Acre referral club',
  [Feature.LBGVerifications]: 'Enable/Disable LBG verifications',
  [Feature.ProtectionAdviceDisclaimer]: 'Enable/Disable protection advice disclaimer',
  [Feature.ExtMarketingReports]: 'Enable/Disable ext marketing reports',
  [Feature.ClientPortalChangesModal]: 'Enable/Disable Client Portal Changes Modal',
  [Feature.MeetingAI]: 'Enable/Disable access to the meeting recorder and analysis functions',
  [Feature.ProtectionAddendum]: 'Enable/Disable System Identified Protection Addendum Letter',
}

export const flags = {
  [FlagType.Feature]: {
    [Feature.Fullstory]: configFlags.get(Feature.Fullstory) || false,
    [Feature.MI]: configFlags.get(Feature.MI) || true,
    //HideClientList is always intended to be a per-org setting so cannot be set using global config.
    [Feature.HideClientList]: false,
    [Feature.NewProtectionSourcing]: configFlags.get(Feature.NewProtectionSourcing) || true,
    [Feature.AddIntroducer]: configFlags.get(Feature.AddIntroducer) || true,
    [Feature.InviteToScheduleAppointment]: configFlags.get(Feature.InviteToScheduleAppointment) || false,
    [Feature.Conveyancing]: configFlags.get(Feature.Conveyancing) || true,
    [Feature.KbaFlow]: configFlags.get(Feature.KbaFlow) || false,
    [Feature.Network]: false,
    [Feature.BulkIntroducers]: configFlags.get(Feature.BulkIntroducers) || false,
    [Feature.Integrations]: configFlags.get(Feature.Integrations) || false,
    [Feature.CreditReportCp]: configFlags.get(Feature.CreditReportCp) || true,
    [Feature.TurnOffReportProblem]: configFlags.get(Feature.TurnOffReportProblem) || false,
    [Feature.SmartlookCP]: configFlags.get(Feature.SmartlookCP) || false,
    [Feature.Lender]: {
      [LenderFlag.Santander]: false,
      [LenderFlag.NatWest]: false,
      [LenderFlag.Nationwide]: true,
      [LenderFlag.Lloyds]: true,
      [LenderFlag.NationwideFMA]: true,
    },
    [Feature.ReferWills]: configFlags.get(Feature.ReferWills) || false,
    [Feature.ReferWillsCpOnly]: configFlags.get(Feature.ReferWillsCpOnly) || false,
    [Feature.Surveying]: true,
    [Feature.SurveyingCp]: configFlags.get(Feature.SurveyingCp) || true,
    [Feature.PreviewMode]: configFlags.get(Feature.PreviewMode) || false,
    [Feature.NeedAPro]: configFlags.get(Feature.NeedAPro) || false,
    [Feature.MovingCompany]: configFlags.get(Feature.MovingCompany) || false,
    [Feature.ConveyancingClientPortal]: configFlags.get(Feature.ConveyancingClientPortal) || false,
    [Feature.HomebuyersChecklist]: configFlags.get(Feature.HomebuyersChecklist) || true,
    [Feature.AccountingSearchMode]: configFlags.get(Feature.AccountingSearchMode) || false,
    [Feature.HideLandRegistryPurchase]: configFlags.get(Feature.HideLandRegistryPurchase) || false,
    [Feature.AdditionalCaseFilters]: configFlags.get(Feature.AdditionalCaseFilters) || true,
    [Feature.ReportCommissions]: configFlags.get(Feature.ReportCommissions) || false,
    [Feature.Dashboards]: false,
    [Feature.HidePredictedRevenue]: configFlags.get(Feature.HidePredictedRevenue) || false,
    [Feature.Payments]: configFlags.get(Feature.Payments) || false,
    [Feature.CaseSidebarDrawer]: configFlags.get(Feature.CaseSidebarDrawer) || false,
    [Feature.AddingManualGI]: configFlags.get(Feature.AddingManualGI) || false,
    [Feature.StandaloneGI]: configFlags.get(Feature.StandaloneGI) || true,
    [Feature.EnhancedAutoRefer]: configFlags.get(Feature.EnhancedAutoRefer) || false,
    [Feature.HideOutOfPanelProcs]: configFlags.get(Feature.HideOutOfPanelProcs) || false,
    [Feature.HomeInOne]: configFlags.get(Feature.HomeInOne) || false,
    [Feature.CommissionOptions]: configFlags.get(Feature.CommissionOptions) || true,
    [Feature.MultiOrgReport]: configFlags.get(Feature.MultiOrgReport) || false,
    [Feature.NewPanelManagementAccordion]: configFlags.get(Feature.NewPanelManagementAccordion) || false,
    [Feature.AdvancedPermissions]: configFlags.get(Feature.AdvancedPermissions) || false,
    [Feature.ConveyancingScotland]: configFlags.get(Feature.ConveyancingScotland) || false,
    [Feature.XlsReport]: configFlags.get(Feature.XlsReport) || false,
    [Feature.DataSecurityReport]: configFlags.get(Feature.DataSecurityReport) || false,
    [Feature.AllowARToggleIncludeDirect]: configFlags.get(Feature.AllowARToggleIncludeDirect) || false,
    [Feature.DocumentRequirements]: configFlags.get(Feature.DocumentRequirements) || false,
    [Feature.CreditRefresh]: configFlags.get(Feature.CreditRefresh) || false,
    [Feature.RiskReport]: configFlags.get(Feature.RiskReport) || false,
    [Feature.CaseClientSurvey]: configFlags.get(Feature.CaseClientSurvey) || false,
    [Feature.PmiOnly]: configFlags.get(Feature.PmiOnly) || false,
    [Feature.MMDConveyancing]: configFlags.get(Feature.MMDConveyancing) || false,
    [Feature.HealthFactFindCp]: configFlags.get(Feature.HealthFactFindCp) || false,
    [Feature.SbgRmar]: configFlags.get(Feature.SbgRmar) || false,
    [Feature.ClientUpdateBanner]: configFlags.get(Feature.ClientUpdateBanner) || false,
    [Feature.ReferralPartnerships]: configFlags.get(Feature.ReferralPartnerships) || false,
    [Feature.ReferralPartnershipsSettings]: configFlags.get(Feature.ReferralPartnershipsSettings) || false,
    [Feature.NewHealthAndProtectionPage]: configFlags.get(Feature.NewHealthAndProtectionPage) || false,
    [Feature.EidvDonors]: configFlags.get(Feature.EidvDonors) || false,
    [Feature.LV]: configFlags.get(Feature.LV) || false,
    [Feature.NewProductPage]: configFlags.get(Feature.NewProductPage) || false,
    [Feature.MockModeEidv]: configFlags.get(Feature.MockModeEidv) || false,
    [Feature.ProtectionNeedsSidebar]: configFlags.get(Feature.ProtectionNeedsSidebar) || false,
    [Feature.ProtectionV2]: configFlags.get(Feature.ProtectionV2) || true,
    [Feature.ProtectionIPipeline]: configFlags.get(Feature.ProtectionIPipeline) || false,
    [Feature.MistralDocumentUpload]: configFlags.get(Feature.MistralDocumentUpload) || false,
    [Feature.ProtectionV2UsePanel]: configFlags.get(Feature.ProtectionV2UsePanel) || false,
    [Feature.AcreReferralClub]: configFlags.get(Feature.AcreReferralClub) || false,
    [Feature.LBGVerifications]: configFlags.get(Feature.LBGVerifications) || false,
    [Feature.ProtectionAdviceDisclaimer]: configFlags.get(Feature.ProtectionAdviceDisclaimer) || false,
    [Feature.ExtMarketingReports]: configFlags.get(Feature.ExtMarketingReports) || false,
    [Feature.ClientPortalChangesModal]: configFlags.get(Feature.ClientPortalChangesModal) || false,
    [Feature.MeetingAI]: configFlags.get(Feature.MeetingAI) || false,
    [Feature.ProtectionAddendum]: configFlags.get(Feature.ProtectionAddendum) || false,
  },
  [FlagType.Automation]: {
    [Automation.InviteClient]: false,
  },
  [FlagType.Untracked]: {
    [Untracked.ARIsTradingStyle]: configFlags.get(Untracked.ARIsTradingStyle) || false,
    [Untracked.ARIsSubdivision]: configFlags.get(Untracked.ARIsSubdivision) || false,
  },
  [FlagType.Club]: {
    [Club.PMS13]: false,
    [Club.PMSIPFA]: false,
    [Club.PMSEXT]: false,
    [Club.PMSDIGITAL]: false,
    [Club.FPG]: false,
    [Club.RightMortgage]: false,
    [Club.MMD]: false,
    [Club.CONNELLS]: false,
    [Club.Sesame]: false,
  },
}

// Used for testing purposes
export const flagsAllEnabled = {
  [FlagType.Feature]: {
    [Feature.Fullstory]: true,
    [Feature.MI]: true,
    [Feature.HideClientList]: true,
    [Feature.NewProtectionSourcing]: true,
    [Feature.AddIntroducer]: true,
    [Feature.InviteToScheduleAppointment]: true,
    [Feature.Conveyancing]: true,
    [Feature.KbaFlow]: true,
    [Feature.Network]: true,
    [Feature.BulkIntroducers]: true,
    [Feature.Integrations]: true,
    [Feature.CreditReportCp]: false,
    [Feature.TurnOffReportProblem]: false,
    [Feature.SmartlookCP]: false,
    [Feature.Lender]: {
      [LenderFlag.Santander]: false,
      [LenderFlag.NatWest]: false,
      [LenderFlag.Nationwide]: true,
      [LenderFlag.Lloyds]: false,
      [LenderFlag.NationwideFMA]: true,
    },
    [Feature.Surveying]: true,
    [Feature.SurveyingCp]: true,
    [Feature.ReferWills]: false,
    [Feature.ReferWillsCpOnly]: false,
    [Feature.Dashboards]: true,
    [Feature.PreviewMode]: false,
    [Feature.NeedAPro]: false,
    [Feature.MovingCompany]: false,
    [Feature.ConveyancingClientPortal]: false,
    [Feature.HomebuyersChecklist]: true,
    [Feature.AccountingSearchMode]: false,
    [Feature.AdditionalCaseFilters]: false,
    [Feature.HidePredictedRevenue]: false,
    [Feature.HideLandRegistryPurchase]: false,
    [Feature.Payments]: true,
    [Feature.CaseSidebarDrawer]: true,
    [Feature.AddingManualGI]: true,
    [Feature.StandaloneGI]: true,
    [Feature.ReportCommissions]: false,
    [Feature.EnhancedAutoRefer]: false,
    [Feature.HideOutOfPanelProcs]: false,
    [Feature.HomeInOne]: true,
    [Feature.CommissionOptions]: false,
    [Feature.MultiOrgReport]: false,
    [Feature.NewPanelManagementAccordion]: false,
    [Feature.ConveyancingScotland]: true,
    [Feature.AdvancedPermissions]: false,
    [Feature.XlsReport]: true,
    [Feature.DataSecurityReport]: true,
    [Feature.AllowARToggleIncludeDirect]: false,
    [Feature.DocumentRequirements]: false,
    [Feature.CreditRefresh]: false,
    [Feature.RiskReport]: false,
    [Feature.CaseClientSurvey]: false,
    [Feature.PmiOnly]: false,
    [Feature.MMDConveyancing]: false,
    [Feature.HealthFactFindCp]: false,
    [Feature.SbgRmar]: false,
    [Feature.ClientUpdateBanner]: true,
    [Feature.ReferralPartnerships]: true,
    [Feature.ReferralPartnershipsSettings]: true,
    [Feature.NewHealthAndProtectionPage]: false,
    [Feature.EidvDonors]: false,
    [Feature.LV]: true,
    [Feature.NewProductPage]: false,
    [Feature.MockModeEidv]: false,
    [Feature.ProtectionNeedsSidebar]: false,
    [Feature.ProtectionV2]: false,
    [Feature.ProtectionIPipeline]: false,
    [Feature.MistralDocumentUpload]: false,
    [Feature.ProtectionV2UsePanel]: false,
    [Feature.AcreReferralClub]: false,
    [Feature.LBGVerifications]: false,
    [Feature.ProtectionAdviceDisclaimer]: false,
    [Feature.ExtMarketingReports]: false,
    [Feature.ClientPortalChangesModal]: false,
    [Feature.MeetingAI]: false,
    [Feature.ProtectionAddendum]: false,
  },
  [FlagType.Automation]: {
    [Automation.InviteClient]: false,
  },
  [FlagType.Untracked]: {
    [Untracked.ARIsTradingStyle]: false,
    [Untracked.ARIsSubdivision]: false,
  },
  [FlagType.Club]: {
    [Club.PMS13]: true,
    [Club.PMSIPFA]: true,
    [Club.PMSDIGITAL]: false,
    [Club.PMSEXT]: false,
    [Club.FPG]: true,
    [Club.RightMortgage]: true,
    [Club.MMD]: true,
    [Club.CONNELLS]: true,
    [Club.Sesame]: true,
  },
}

export type Flags = InferType<typeof flags>

// Make the `network` feature flag type optional, as AR organizations do not require this feature flag
export type FlagsWithOptionalNetwork = Omit<Flags, 'feature'> & {
  feature: Omit<Flags['feature'], 'network'> & {
    network?: boolean
  }
}

export type FlagInput = { [name: string]: boolean | FlagInput }
